import { Injectable } from '@angular/core';
import { Configuration } from '../../../app.constants';
import { environment } from 'src/environments/environment';
import { isUri } from 'valid-url';
import { ObjectTypes } from '../enums/objectTypes';
import { Router } from '@angular/router';
import { Account } from '../models/account';
import { IncidentItemTypes } from '../../incidents/enums/incidentItemTypes';
import { RiskTypes } from '../../risk/enums/riskTypes';
import { AuthenticationService } from './authentication.service';
@Injectable({
  providedIn: 'root',
})
export class UrlService {
  objectTypes = ObjectTypes;
  incidentItemTypes = IncidentItemTypes;
  account: Account;
  constructor(private configuration: Configuration, private readonly router: Router,
    private readonly authenticationService: AuthenticationService) {
    this.account = authenticationService.getCurrentAccount();
  }

  public buildResourceUrl(url: string): string {
    if (!url) {
      return environment.DEFAULT_AVATAR;
    }

    if (isUri(url)) {
      return url;
    }

    if (url.startsWith('/') && this.configuration.buildStaticFileUrl('/').endsWith('/')) {
      return this.configuration.buildStaticFileUrl(url.substring(1));
    }

    if (!url.startsWith('/') && !this.configuration.buildStaticFileUrl('/').endsWith('/')) {
      return this.configuration.buildStaticFileUrl('/' + url);
    }

    return this.configuration.buildStaticFileUrl(url);
  }

  /**
   * build Account Hub Logo URl to be shown in child account
   */
  public builLogoUrlForHub(url: string, hubAccountUrl: string): string {
    return `${hubAccountUrl}/${this.configuration.ResourceFolderName}/assets/clients/logo/${url}`;
  }

  public navigateToObjectDetailsPage(
    objectType: ObjectTypes,
    objectSubType: number,
    objectId: number,
    openInNewWindow: boolean = true,
    includeAttachmentsFragment = false
  ) {
    if (!this.account) {
      this.account = this.authenticationService.getCurrentAccount();
    }

    const routerLink = this.getLinkToDetailsPage(objectId, objectType, objectSubType);

    const isALogItem = objectType === this.objectTypes.IncidentItem && objectSubType === this.incidentItemTypes.Log;

    if (routerLink) {
      let url: string;

      if (!isALogItem) {
        if (includeAttachmentsFragment) {
          url = this.router.serializeUrl(this.router.createUrlTree([routerLink], { fragment: `attachments` }));
        } else {
          url = this.router.serializeUrl(this.router.createUrlTree([routerLink]));
        }
      } else {
        url = this.router.serializeUrl(this.router.createUrlTree([routerLink], { fragment: `${objectId}` }));
      }

      if (openInNewWindow) {
        window.open(url, '_blank');
      } else {
        void this.router.navigate([url]);
      }
    }
  }

  public replaceAccountUIDInLink(url: string): string {
    if (url.includes(':accountId')) {
      url = url.replace(':accountId', this.account.accountUID);
    }
    return url;
  }

  public getLinkToDetailsPage(objectId: number, objectType: ObjectTypes, objectSubType: number) {

    let routerLink = '';
    switch (objectType) {
      case ObjectTypes.Task:
        routerLink = `/${this.account.accountUID}/planning/list/tasks/${objectId}`;
        break;
      case ObjectTypes.IncidentItem:
        if (objectSubType === IncidentItemTypes.Incident) {
          routerLink = `/${this.account.accountUID}/control/list/${objectId}`
        }

        if (objectSubType === IncidentItemTypes.Log) {
          routerLink = `/${this.account.accountUID}/control/logs`
        }

        break;
      case ObjectTypes.Job:
        routerLink = `/${this.account.accountUID}/control/jobs/${objectId}`;
        break;
      case ObjectTypes.Project:
        routerLink = `/${this.account.accountUID}/planning/list/projects/${objectId}`;
        break;
      case ObjectTypes.Risk:
        if (objectSubType === RiskTypes.Risk) {
          routerLink = `/${this.account.accountUID}/risk/risks-issues/risk/${objectId}`;
        } else if (objectSubType === RiskTypes.Issue) {
          routerLink = `/${this.account.accountUID}/risk/risks-issues/issue/${objectId}`;
        } else if (objectSubType === RiskTypes.Opportunity) {
          routerLink = `/${this.account.accountUID}/risk/risks-issues/opportunity/${objectId}`;
        } else {
          routerLink = `/${this.account.accountUID}/risk/risks-issues/all/${objectId}`;
        }
        break;
      case ObjectTypes.Risk_Assesment:
        routerLink = `/${this.account.accountUID}/risk/risk-assessments/assessment/${objectId}`;
        break;
      case ObjectTypes.Runsheet:
        routerLink = `/${this.account.accountUID}/runsheets/list/${objectId}`;
        break;
      case ObjectTypes.Indicator:
        routerLink = `${this.account.accountUID}/sustainability/indicators/${objectId}`;
        break;
      case ObjectTypes.Template_Risk:
        routerLink = `/${this.account.accountUID}/risk/risk-assessments/template/${objectId}`;
        break;
    }

    return routerLink;
  }

  public getAccountPrivacyPolicyURL(account: Account) {
    return account.useWhitelabeling && !!account.alternativePrivacyPolicyURL
      ? account.alternativePrivacyPolicyURL
      : 'https://www.wetrack.com/privacy-policy';
  }

  public getAccountTermsOfUseURL(account: Account) {
    return account.useWhitelabeling && !!account.alternativeTermsOfUseURL
      ? account.alternativeTermsOfUseURL
      : 'https://www.wetrack.com/terms-of-use';
  }

  public navigateToListPage(
    objectType: ObjectTypes,
    objectSubType: number,
    openInNewWindow: boolean = true
  ) {
    if (!this.account) {
      this.account = this.authenticationService.getCurrentAccount();
    }

    const routerLink = this.getLinkToListPage(objectType, objectSubType);

    if (routerLink) {
      let url: string;

      url = this.router.serializeUrl(this.router.createUrlTree([routerLink]));

      if (openInNewWindow) {
        window.open(url, '_blank');
      } else {
        void this.router.navigate([url]);
      }
    }
  }

  public getLinkToListPage(objectType: ObjectTypes, objectSubType: number) {

    let routerLink = '';
    switch (objectType) {
      case ObjectTypes.Task:
        routerLink = `/${this.account.accountUID}/planning/list`;
        break;
      case ObjectTypes.IncidentItem:
        if (objectSubType === IncidentItemTypes.Incident) {
          routerLink = `/${this.account.accountUID}/control/list`
        }

        if (objectSubType === IncidentItemTypes.Log) {
          routerLink = `/${this.account.accountUID}/control/logs`
        }

        break;
      case ObjectTypes.Job:
        routerLink = `/${this.account.accountUID}/control/jobs`;
        break;
      case ObjectTypes.Project:
        routerLink = `/${this.account.accountUID}/planning/list`;
        break;
      case ObjectTypes.Risk:
        if (objectSubType === RiskTypes.Risk) {
          routerLink = `/${this.account.accountUID}/risk/risks-issues`;
        } else if (objectSubType === RiskTypes.Issue) {
          routerLink = `/${this.account.accountUID}/risk/risks-issues`;
        } else if (objectSubType === RiskTypes.Opportunity) {
          routerLink = `/${this.account.accountUID}/risk/risks-issues`;
        }
        break;
      case ObjectTypes.Risk_Assesment:
        routerLink = `/${this.account.accountUID}/risk/risk-assessments`;
        break;
      case ObjectTypes.Runsheet:
        routerLink = `/${this.account.accountUID}/runsheets/list`;
        break;
      case ObjectTypes.Indicator:
        routerLink = `${this.account.accountUID}/sustainability/indicators`;
        break;
      case ObjectTypes.Template_Risk:
        routerLink = `/${this.account.accountUID}/risk/risk-assessments`;
        break;
      case ObjectTypes.Event:
        routerLink = `/${this.account.accountUID}/events/calendar`;
        break;
    }

    return routerLink;
  }

}
